import { BitcoinConnector } from "../../../../core/types/bitcoin";
import {
  BitcoinConnectorInvalidAccountError,
  BitcoinConnectorProviderNotFoundError,
  BitcoinConnectorTransactionFailedError,
} from "../errors";

interface UnisatSignPsbtOptions {
  autoFinalized?: boolean;
  toSignInputs?: Array<{
    index: number;
    address?: string;
    publicKey?: string;
    sighashTypes?: number[];
    disableTweakSigner?: boolean;
  }>;
}

interface UnisatProvider {
  sendBitcoin: (to: string, amount: number) => Promise<string>;
  requestAccounts: () => Promise<string[]>;
  signPsbt: (
    psbtHex: string,
    options?: UnisatSignPsbtOptions
  ) => Promise<string>;
  pushPsbt: (psbtHex: string) => Promise<string>;
}

export class UnisatConnector implements BitcoinConnector {
  public get getProvider(): UnisatProvider {
    const provider = (window as any)?.unisat;

    if (!provider) {
      throw new BitcoinConnectorProviderNotFoundError("Unisat");
    }

    return provider;
  }

  async sendBTC(to: string, amount: number) {
    const txHash = await this.getProvider.sendBitcoin(to, amount);

    if (!txHash) {
      throw new BitcoinConnectorTransactionFailedError();
    }

    return { txHash };
  }

  async requestAccount() {
    const accounts: string[] = await this.getProvider.requestAccounts();

    const account = accounts[0];

    if (typeof account !== "string") {
      throw new BitcoinConnectorInvalidAccountError();
    }

    return {
      address: account,
    };
  }

  async signPsbt(psbtHex: string): Promise<{ txHash: string }> {
    // UniSat signs and, with `autoFinalized` (the default), finalizes every
    // input, returning the signed PSBT as a hex string.
    // https://docs.unisat.io/dev/unisat-developer-center/unisat-wallet/api-list
    const signedPsbtHex = await this.getProvider.signPsbt(psbtHex, {
      autoFinalized: true,
    });

    // `pushPsbt` extracts the finalized transaction from the signed PSBT and
    // broadcasts it, returning the transaction id.
    const txHash = await this.getProvider.pushPsbt(signedPsbtHex);

    if (!txHash) {
      throw new BitcoinConnectorTransactionFailedError();
    }

    return { txHash };
  }
}
